<script lang="ts">
    import { setTypedContext } from "@lib/svelte-typed-context";
    import { createEventDispatcher, onMount } from "svelte";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Icon from "@components/Icon.svelte";

    import { videoControllerToken } from "./context_tokens";
    import VideoPlayerPlayIcon from "./icons/VideoPlayerPlayIcon.svelte";
    import type { VideoPlayerSource, VideoPlayerTextTrack } from "./types";
    import {
        LOADING_STATUS,
        PAUSE_STATUS,
        PLAYING_STATUS,
        VideoController,
    } from "./VideoController";
    import VideoPlayerUIPanel from "./VideoPlayerUIPanel.svelte";
    import VideoPlayerUISpinner from "./VideoPlayerUISpinner.svelte";

    export let sources: VideoPlayerSource[];
    export let tracks: VideoPlayerTextTrack[] = [];
    export let poster: string | undefined = undefined;
    export let autoplay = false;
    export let showUIPanel = true;
    export let muted = false;
    export let uiPannelAutoHide = false;
    export let showVideoControll = true;
    export let showSpinner = true;
    const videoController = new VideoController();
    const status$ = videoController.status$;
    setTypedContext(videoControllerToken, videoController);
    let videoPlayer: HTMLDivElement | null = null;
    let videoElement: HTMLVideoElement | null = null;
    let hideUiPanel = uiPannelAutoHide;
    const dispatch = createEventDispatcher();
    const hasFullscreenElementOnPage = (): boolean =>
        !!document.fullscreenElement;
    const handleLoad = () => {
        dispatch("videoLoaded");
    };
    const handlePlayPause = () => {
        switch ($status$) {
            case PLAYING_STATUS:
                videoController.pause();
                break;
            case PAUSE_STATUS:
                videoController.play();
                break;
            default:
        }
    };
    const handleMouseEnter = () => {
        if (uiPannelAutoHide) hideUiPanel = false;
    };
    const handleMouseLeave = () => {
        if (uiPannelAutoHide) hideUiPanel = true;
    };
    onMount(() => {
        if (videoElement) {
            videoController.connect(videoElement);
        }
        videoController.fullscreen$.subscribe((enabled) => {
            if (videoPlayer) {
                if (enabled) {
                    videoPlayer.requestFullscreen(); /* Checking of document.fullscreenElement needed on initial rendering of
    component and on exiting from fullscreen mode on ESC-key */
                } else if (hasFullscreenElementOnPage()) {
                    document.exitFullscreen();
                }
            } else {
                throw new Error();
            }
        });
        dispatch("initialized", { videoController });
    });
    /*
    This special event-handler for reacting to exit from fullscreen mode by ESC-key
    */ const handleFullscreenChange = () => {
        if (!hasFullscreenElementOnPage()) {
            videoController.leaveFullscreen();
        }
    };
</script>

<div
    bind:this={videoPlayer}
    on:fullscreenchange={handleFullscreenChange}
    on:mouseenter={handleMouseEnter}
    on:mouseleave={handleMouseLeave}
    class="gw-video-player"
>
    {#if showVideoControll}
        <BaseButton
            on:click={handlePlayPause}
            class="gw-video-player__controller"
            aria-label={$status$ === PLAYING_STATUS
                ? "Pause Video"
                : "Play Video"}
            aria-live="polite"
            aria-disabled={$status$ === LOADING_STATUS}
            disabled={$status$ === LOADING_STATUS}
        >
            {#if $status$ === PAUSE_STATUS}
                <Icon height={40} width={40}>
                    <VideoPlayerPlayIcon />
                </Icon>
            {/if}
        </BaseButton>
    {/if}

    <video
        bind:this={videoElement}
        class="gw-video-player__video"
        {autoplay}
        {poster}
        {muted}
        on:loadeddata={handleLoad}
    >
        {#each sources as source}
            <source type={source.mime} src={source.src} />
        {/each}
        {#each tracks as track}
            <track
                default={track.isDefault}
                kind={track.kind}
                src={track.src}
                srclang={track.lang}
            />
        {/each}
        <track kind="captions" />
        <p>
            Your browser doesn't support HTML5 video. Here is a
            <a href={sources[0].src}> link to the video </a> instead.
        </p>
    </video>
    {#if showUIPanel}
        <VideoPlayerUIPanel {hideUiPanel} />
    {/if}
    {#if $status$ === LOADING_STATUS && showSpinner}
        <VideoPlayerUISpinner />
    {/if}
</div>

<style>
    .gw-video-player {
        display: flex;
        position: relative;
        width: 100%;
    }

    .gw-video-player__video {
        display: block;
        width: 100%;
    }

    .gw-video-player :global(.gw-video-player__controller) {
        position: absolute;
        z-index: 1;
        width: 100%;
        height: 100%;
        opacity: 0.5;
    }
</style>
